/**
 * Column top-N — counts the most frequent non-null values in a column.
 * Pure function, no Electron dependencies.
 *
 * Used by:
 * - ColumnProfileBadge popover (top 5 categorical values)
 * - DataProfileTopNCard (configurable: 5/10/25/50/100)
 */

const DEFAULT_DISPLAY_LENGTH = 60;

export type TopNEntry = {
  value: unknown;
  /** Truncated string for UI rendering. */
  displayValue: string;
  count: number;
  /** Percent of non-null rows (0–100). */
  percent: number;
};

export type TopNResult = {
  entries: TopNEntry[];
  /** Sum of counts of values not in `entries` (still non-null). */
  otherCount: number;
  nullCount: number;
};

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str: string;
  if (value instanceof Date) {
    str = value.toISOString();
  } else if (typeof value === 'object') {
    try {
      str = JSON.stringify(value);
    } catch {
      str = String(value);
    }
  } else {
    str = String(value);
  }
  if (str.length > DEFAULT_DISPLAY_LENGTH) {
    return str.slice(0, DEFAULT_DISPLAY_LENGTH - 3) + '...';
  }
  return str;
}

/**
 * Count occurrences of each distinct non-null value, return the top `n` by count.
 * Numeric and string-typed values that look the same (e.g. 99.99 and "99.99") are
 * treated as distinct (Map equality is reference/value-strict).
 */
export function computeColumnTopN(
  rows: Record<string, unknown>[],
  columnId: string,
  n: number,
): TopNResult {
  const safeN = Math.max(1, Math.floor(n));
  let nullCount = 0;
  let nonNullCount = 0;

  const counts = new Map<unknown, number>();

  for (let i = 0; i < rows.length; i++) {
    const value = rows[i]?.[columnId];
    if (value === null || value === undefined) {
      nullCount++;
      continue;
    }
    nonNullCount++;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  if (counts.size === 0) {
    return { entries: [], otherCount: 0, nullCount };
  }

  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, safeN);

  let otherCount = 0;
  for (let i = safeN; i < sorted.length; i++) {
    otherCount += sorted[i][1];
  }

  const entries: TopNEntry[] = top.map(([value, count]) => ({
    value,
    displayValue: formatDisplayValue(value),
    count,
    percent: nonNullCount > 0 ? (count / nonNullCount) * 100 : 0,
  }));

  return { entries, otherCount, nullCount };
}
